/**
 * Parent ↔ School communications — shared types and constants.
 *
 * Threads live in `reportParentMessages`; individual messages live in
 * the `posts` subcollection under each thread.
 *
 * Security model (enforced in firestore.rules):
 *   - A parent may create/read ONLY threads where parentUid == their
 *     own uid AND studentId is one of their linked children.
 *   - A parent may post only in their own threads. Never edit/delete.
 *   - Staff (admin/head/reception) read all, reply, change status.
 *   - proposedChanges.changeStatus is staff-only.
 *   - Approving a detail change writes to the student record via the
 *     approveParentDetailChange Cloud Function (server-side, audited),
 *     NOT client-side — keeps the student-write surface minimal.
 */

export type MessageCategory =
  | 'consultation' // request a meeting/call with a subject teacher
  | 'marks_query' // question about marks / performance
  | 'detail_correction' // "this detail in the portal is wrong"
  | 'detail_update' // "please update this detail (address/doctor/etc.)"
  | 'onboarding' // registration / portal-access / add-a-child help
  | 'general'; // anything else

export type ThreadStatus =
  | 'awaiting_school' // parent sent, school hasn't replied
  | 'awaiting_parent' // school replied, waiting on parent
  | 'resolved'
  | 'archived';

export type ChangeStatus = 'pending' | 'approved' | 'rejected';

/**
 * Whitelisted fields a parent may request to update via a
 * detail_update message. The key is what the parent picks; the
 * `path` is where it lives under the student document. The Cloud
 * Function only ever writes to these paths — never arbitrary ones.
 */
export interface ChangeableField {
  key: string;
  label: string;
  /** dot-path under the student doc, e.g. "admissions.address" */
  path: string;
  /** simple input hint */
  kind: 'text' | 'tel' | 'email';
}

export const CHANGEABLE_FIELDS: ChangeableField[] = [
  { key: 'address', label: 'Home address', path: 'admissions.address', kind: 'text' },
  { key: 'doctorName', label: "Doctor's name", path: 'admissions.medical.doctorName', kind: 'text' },
  { key: 'doctorPhone', label: "Doctor's phone", path: 'admissions.medical.doctorPhone', kind: 'tel' },
  { key: 'medicalAidProvider', label: 'Medical aid provider', path: 'admissions.medical.medicalAidProvider', kind: 'text' },
  { key: 'medicalAidNumber', label: 'Medical aid number', path: 'admissions.medical.medicalAidNumber', kind: 'text' },
  { key: 'allergies', label: 'Allergies', path: 'admissions.medical.allergies', kind: 'text' },
  { key: 'chronicConditions', label: 'Chronic conditions', path: 'admissions.medical.chronicConditions', kind: 'text' },
  { key: 'medications', label: 'Current medications', path: 'admissions.medical.medications', kind: 'text' },
  { key: 'emergencyContactName', label: 'Emergency contact name', path: 'admissions.emergencyContactName', kind: 'text' },
  { key: 'emergencyContactPhone', label: 'Emergency contact phone', path: 'admissions.emergencyContactPhone', kind: 'tel' },
];

export function getChangeableField(key: string): ChangeableField | undefined {
  return CHANGEABLE_FIELDS.find((f) => f.key === key);
}

export interface ProposedChange {
  /** key from CHANGEABLE_FIELDS */
  fieldKey: string;
  fieldLabel: string;
  /** value currently on record (snapshot at request time, for display) */
  currentValue: string;
  /** value the parent is requesting */
  newValue: string;
}

export interface ParentMessageThread {
  id: string;
  studentId: string;
  studentName: string;
  parentUid: string;
  parentName: string;
  parentEmail: string;
  category: MessageCategory;
  /** optional: subject teacher context (subjectId) for consultation/marks */
  subjectContext?: string;
  subjectContextLabel?: string;
  /** short thread title */
  subject: string;
  status: ThreadStatus;
  /** present only for detail_update threads */
  proposedChanges?: ProposedChange[];
  changeStatus?: ChangeStatus;
  /** who resolved the change + when (set by the Cloud Function) */
  changeActionedBy?: string;
  changeActionedAt?: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  /** denormalised for inbox sorting/badges */
  lastMessagePreview?: string;
  lastMessageSide?: 'parent' | 'school';
  /** unread flags */
  unreadBySchool?: boolean;
  unreadByParent?: boolean;
}

export interface ParentMessagePost {
  id: string;
  authorUid: string;
  authorName: string;
  authorSide: 'parent' | 'school';
  text: string;
  createdAt: string;
}

export const CATEGORY_LABELS: Record<MessageCategory, string> = {
  consultation: 'Teacher consultation request',
  marks_query: 'Query on marks / performance',
  detail_correction: 'Flag incorrect student detail',
  detail_update: 'Update student details',
  onboarding: 'Onboarding / registration help',
  general: 'General enquiry',
};

export const STATUS_LABELS: Record<ThreadStatus, string> = {
  awaiting_school: 'Awaiting school',
  awaiting_parent: 'Awaiting your reply',
  resolved: 'Resolved',
  archived: 'Archived',
};

/* Ported from Al-Nur with the student portal (2026-10-29). */
/* ══════════════════════════════════════════════════════════════════
   STUDENT ↔ TEACHER MESSAGES
   ------------------------------------------------------------------
   A SEPARATE COLLECTION (`reportStudentMessages`), deliberately, rather
   than a flag on the parent threads above. A pupil must never be able to
   read what a teacher wrote to their parent — about marks, conduct or
   fees — and a shared collection would make that a filter somebody has to
   remember rather than a wall the rules enforce. It is the same reasoning
   that keeps flashcards out of the mastery collection.

   Phase 1 is teacher-initiated: a teacher writes to one pupil or a class
   (one thread per pupil, as assignments expand a class), and the pupil
   replies. A pupil cannot open a thread — that needs a decision about who
   fields it, and an empty inbox is better than an unanswered one.
   ═══════════════════════════════════════════════════════════════════ */

export type StudentMessageCategory =
  | 'work'      // homework, classwork, corrections
  | 'praise'    // well done — the positive counterpart of discipline
  | 'admin'     // logins, forms, timetable, arrangements
  | 'general';

export type StudentThreadStatus =
  | 'awaiting_student'  // teacher wrote, pupil has not answered
  | 'awaiting_school'   // pupil answered, teacher has not
  | 'resolved'
  | 'archived';

export interface StudentMessageThread {
  id: string;
  /** The pupil's RECORD id (reportStudents), which is what the rules match
   *  against `studentAccount.studentId` — never the account uid, because a
   *  pupil can be re-issued a login and keep the same record. */
  studentId: string;
  studentName: string;
  className?: string;
  createdByUid: string;
  createdByName: string;
  createdByRole?: string;
  /** Set when the thread was part of a class-wide send, so a teacher can see
   *  the whole batch rather than 34 unrelated conversations. */
  batchId?: string;
  subject: string;
  category: StudentMessageCategory;
  status: StudentThreadStatus;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  lastMessagePreview?: string;
  lastMessageSide?: 'student' | 'school';
  unreadByStudent?: boolean;
  unreadBySchool?: boolean;
}

export interface StudentMessagePost {
  id: string;
  authorUid: string;
  authorName: string;
  authorSide: 'student' | 'school';
  text: string;
  createdAt: string;
}

export const STUDENT_CATEGORY_LABELS: Record<StudentMessageCategory, string> = {
  work: 'Work',
  praise: 'Well done',
  admin: 'Admin',
  general: 'General',
};
